/**
 * UnassignedVisitsView
 *
 * Demo version: shows mock unassigned visits for the next 7 days.
 * Providers can "take" a visit (removes it from the list locally).
 * Supervisors can "assign" a visit to a provider (also removes it locally).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { addDays, format, startOfDay, setHours, setMinutes } from 'date-fns';

import { useOrgConfig } from '~/providers/OrgConfigProvider';
import { useCurrentOrg } from '~/providers/GraphqlProvider';
import { useHasPermission, Permission } from '~/hooks/permissions';
import { useSnackbar } from '~/hooks/useSnackbar';
import { getMarketListVisits, removeMarketListVisit, subscribeToMarketList, type MarketListVisit } from '~/services/demo/marketListStore';

// ---------------------------------------------------------------------------
// Mock data — realistic unassigned visits based on demo patients
// ---------------------------------------------------------------------------

const today = startOfDay(new Date());

function mockTime(dayOffset: number, hour: number, minute = 0): Date {
  const d = addDays(today, dayOffset);
  return setMinutes(setHours(d, hour), minute);
}

interface MockVisit {
  id: number;
  startTime: Date;
  endTime: Date;
  arrivalWindowStart: Date;
  arrivalWindowEnd: Date;
  patientFirstName: string;
  patientLastName: string;
}

const MOCK_VISITS: MockVisit[] = [
  { id: 1, startTime: mockTime(0, 10, 0), endTime: mockTime(0, 11, 0), arrivalWindowStart: mockTime(0, 9, 0),  arrivalWindowEnd: mockTime(0, 12, 0),  patientFirstName: 'Susan',    patientLastName: 'Anderson-AS3' },
  { id: 2, startTime: mockTime(0, 14, 0), endTime: mockTime(0, 15, 0), arrivalWindowStart: mockTime(0, 13, 0), arrivalWindowEnd: mockTime(0, 16, 0),  patientFirstName: 'Maria',    patientLastName: 'Garcia-AS' },
  { id: 3, startTime: mockTime(1, 9, 0),  endTime: mockTime(1, 10, 0), arrivalWindowStart: mockTime(1, 8, 0),  arrivalWindowEnd: mockTime(1, 11, 0),  patientFirstName: 'Robert',   patientLastName: 'Johnson-AS' },
  { id: 4, startTime: mockTime(1, 11, 0), endTime: mockTime(1, 12, 0), arrivalWindowStart: mockTime(1, 10, 0), arrivalWindowEnd: mockTime(1, 13, 0),  patientFirstName: 'Linda',    patientLastName: 'Lopez-AS' },
  { id: 5, startTime: mockTime(2, 10, 30),endTime: mockTime(2, 11, 30),arrivalWindowStart: mockTime(2, 9, 0),  arrivalWindowEnd: mockTime(2, 12, 0),  patientFirstName: 'James',    patientLastName: 'Wilson-AS' },
  { id: 6, startTime: mockTime(3, 13, 0), endTime: mockTime(3, 14, 0), arrivalWindowStart: mockTime(3, 11, 0), arrivalWindowEnd: mockTime(3, 15, 0),  patientFirstName: 'Jennifer', patientLastName: 'Ramirez-AS' },
  { id: 7, startTime: mockTime(5, 10, 0), endTime: mockTime(5, 11, 0), arrivalWindowStart: mockTime(5, 8, 0),  arrivalWindowEnd: mockTime(5, 14, 0),  patientFirstName: 'Michael',  patientLastName: 'Gonzalez-AS' },
  { id: 8, startTime: mockTime(6, 9, 0),  endTime: mockTime(6, 10, 0), arrivalWindowStart: mockTime(6, 8, 0),  arrivalWindowEnd: mockTime(6, 11, 0),  patientFirstName: 'Dorothy',  patientLastName: 'Jackson-AS' },
];

const MOCK_PROVIDERS = [
  { id: 1, name: 'Sarah Johnson, RN' },
  { id: 2, name: 'Michael Chen, RN' },
  { id: 3, name: 'Dr. Patricia Rodriguez' },
  { id: 4, name: 'James Williams, RN' },
  { id: 5, name: 'Linda Martinez, RN' },
  { id: 6, name: 'Dr. Robert Davis' },
];

// ---------------------------------------------------------------------------

export default function UnassignedVisitsView() {
  const orgConfig = useOrgConfig();
  const currentOrg = useCurrentOrg();
  const myProviderId = currentOrg?.providerId ?? null;
  const canScheduleVisits = useHasPermission(Permission.scheduleVisits) ?? false;
  const canDragEvents = useHasPermission(Permission.createDragEvents) ?? false;
  const isSupervisor = (canScheduleVisits || canDragEvents) && !myProviderId;
  const { setErrorMessage: showSnackbar } = useSnackbar();

  const routineVisitTypeName: string = useMemo(() => {
    const types = Object.values(orgConfig.visitTypes ?? {}) as Array<{ name?: string | null }>;
    const routine = types.find(
      (vt) => vt.name?.toLowerCase().includes('routine') || vt.name?.toLowerCase().includes('paramedic')
    );
    return routine?.name ?? types[0]?.name ?? 'Routine Visit';
  }, [orgConfig.visitTypes]);

  const [dismissed, setDismissed] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState<'patient' | 'date' | 'arrivalWindow'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Market list visits injected by demo scenarios
  const [marketListVisits, setMarketListVisits] = useState<MarketListVisit[]>(() => getMarketListVisits());
  useEffect(() => subscribeToMarketList(() => setMarketListVisits(getMarketListVisits())), []);

  const handleSort = (col: 'patient' | 'date' | 'arrivalWindow') => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
    setPage(0);
  };
  const [assignTarget, setAssignTarget] = useState<MockVisit | null>(null);
  const [selectedProviderId, setSelectedProviderId] = useState<number | ''>('');

  const windowEnd = addDays(today, 8);
  const visits = useMemo(() => {
    const fromMarket: MockVisit[] = marketListVisits.map((ml) => ({
      id: ml.id + 10000, // offset to avoid collision with MOCK_VISITS ids
      startTime: new Date(ml.startTime),
      endTime: new Date(ml.endTime),
      arrivalWindowStart: new Date(ml.arrivalWindowStart),
      arrivalWindowEnd: new Date(ml.arrivalWindowEnd),
      patientFirstName: ml.patientFirstName,
      patientLastName: ml.patientLastName,
      _marketListId: ml.id,
      _visitTypeName: ml.visitTypeName,
    } as MockVisit & { _marketListId: number; _visitTypeName: string }));

    const base = [...MOCK_VISITS, ...fromMarket].filter((v) => !dismissed.has(v.id));
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...base].sort((a, b) => {
      if (sortBy === 'patient') {
        const cmp = a.patientLastName.localeCompare(b.patientLastName) || a.patientFirstName.localeCompare(b.patientFirstName);
        return cmp * dir;
      }
      if (sortBy === 'date') return (a.startTime.getTime() - b.startTime.getTime()) * dir;
      if (sortBy === 'arrivalWindow') return (a.arrivalWindowStart.getTime() - b.arrivalWindowStart.getTime()) * dir;
      return 0;
    });
  }, [dismissed, sortBy, sortDir]);
  const visibleVisits = visits.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const handleTake = (v: MockVisit & { _marketListId?: number }) => {
    setDismissed((prev) => new Set(prev).add(v.id));
    if (v._marketListId != null) removeMarketListVisit(v._marketListId);
    showSnackbar(`Visit for ${v.patientLastName}, ${v.patientFirstName} added to your schedule`, 'success');
  };

  const handleOpenAssign = (v: MockVisit) => {
    setAssignTarget(v);
    setSelectedProviderId('');
  };

  const handleConfirmAssign = () => {
    if (!assignTarget || !selectedProviderId) return;
    const provider = MOCK_PROVIDERS.find((p) => p.id === selectedProviderId);
    const target = assignTarget as MockVisit & { _marketListId?: number };
    if (target._marketListId != null) removeMarketListVisit(target._marketListId);
    setDismissed((prev) => new Set(prev).add(assignTarget.id));
    showSnackbar(
      `Visit for ${assignTarget.patientLastName}, ${assignTarget.patientFirstName} assigned to ${provider?.name}`,
      'success'
    );
    setAssignTarget(null);
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing unassigned visits {format(today, 'MMM d')}–{format(windowEnd, 'MMM d')}
        </Typography>
      </Box>

      {visits.length === 0 ? (
        <Alert severity="success">No unassigned visits in the next 7 days. All visits have providers.</Alert>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortBy === 'patient' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'patient'} direction={sortBy === 'patient' ? sortDir : 'asc'} onClick={() => handleSort('patient')}>
                      Patient
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'date' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'date'} direction={sortBy === 'date' ? sortDir : 'asc'} onClick={() => handleSort('date')}>
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'arrivalWindow' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'arrivalWindow'} direction={sortBy === 'arrivalWindow' ? sortDir : 'asc'} onClick={() => handleSort('arrivalWindow')}>
                      Arrival Window
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Visit Type</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleVisits.map((v) => {
                  const mv = v as MockVisit & { _visitTypeName?: string };
                  const typeName = mv._visitTypeName ?? routineVisitTypeName;
                  const isToday    = format(v.startTime, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
                  const isTomorrow = format(v.startTime, 'yyyy-MM-dd') === format(addDays(today, 1), 'yyyy-MM-dd');

                  return (
                    <TableRow key={v.id} hover>
                      <TableCell>
                        <strong>{v.patientLastName}</strong>, {v.patientFirstName}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {format(v.startTime, 'EEE, MMM d')}
                          {isToday    && <Chip label="Today"    size="small" color="warning" />}
                          {isTomorrow && <Chip label="Tomorrow" size="small" color="info" />}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {format(v.arrivalWindowStart, 'h:mm a')} – {format(v.arrivalWindowEnd, 'h:mm a')}
                      </TableCell>
                      <TableCell>{typeName}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Tooltip title="Take this visit and place it on your schedule">
                            <Button size="small" variant="contained" startIcon={<PersonAddIcon />} onClick={() => handleTake(v)}>
                              Take
                            </Button>
                          </Tooltip>
                          <Tooltip title="Assign this visit to a provider">
                            <Button size="small" variant="outlined" startIcon={<AssignmentIndIcon />} onClick={() => handleOpenAssign(v)}>
                              Assign
                            </Button>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={visits.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setPage(0); setRowsPerPage(parseInt(e.target.value)); }}
          />
        </>
      )}
      <Dialog open={!!assignTarget} onClose={() => setAssignTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Visit</DialogTitle>
        <DialogContent>
          {assignTarget && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {assignTarget.patientLastName}, {assignTarget.patientFirstName} &mdash;{' '}
              {format(assignTarget.startTime, 'EEE, MMM d')},{' '}
              {format(assignTarget.arrivalWindowStart, 'h:mm a')}–{format(assignTarget.arrivalWindowEnd, 'h:mm a')}
            </Typography>
          )}
          <FormControl fullWidth size="small">
            <InputLabel>Provider</InputLabel>
            <Select
              value={selectedProviderId}
              label="Provider"
              onChange={(e) => setSelectedProviderId(e.target.value as number)}
            >
              {MOCK_PROVIDERS.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignTarget(null)}>Cancel</Button>
          <Button variant="contained" disabled={!selectedProviderId} onClick={handleConfirmAssign}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
